import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

import TopHeader from "../../common/Header/TopHeader";

// Import the PatientCalendarView component
import PatientCalendarView from "./PatientCalendarView";

// Corrected imports with error handling
let DetailsTreatments, LaboratoryTreatments, NotesTreatments, DiagnosticImagingTreatments;
let MedicalRecordTreatments, NursingTreatments, MolecularMedicineTreatments;

// Service function imports - you'll need to replace these with your actual service imports
let treatmentDetailsService, laboratoryService, notesService, diagnosticImagingService;
let medicalRecordService, nursingService, molecularMedicineService;

try {
  DetailsTreatments = require("../Components/DetailsTreatment/DetailsTreatments").default;
} catch (e) {
  DetailsTreatments = () => <div className="alert alert-warning">DetailsTreatments component not available</div>;
}

try {
  LaboratoryTreatments = require("../Components/Laboratory/LaboratoryTreatments").default;
} catch (e) {
  LaboratoryTreatments = () => <div className="alert alert-warning">LaboratoryTreatments component not available</div>;
}

try {
  NotesTreatments = require("../Components/Notes/NotesTreatments").default;
} catch (e) {
  NotesTreatments = () => <div className="alert alert-warning">NotesTreatments component not available</div>;
}

try {
  DiagnosticImagingTreatments = require("../Components/DiagnosticImaging/DiagnosticImagingTreatments").default;
} catch (e) {
  DiagnosticImagingTreatments = () => <div className="alert alert-warning">DiagnosticImagingTreatments component not available</div>;
}

try {
  MedicalRecordTreatments = require("../Components/MedicalRecord/MedicalRecordTreatments").default;
} catch (e) {
  MedicalRecordTreatments = () => <div className="alert alert-warning">MedicalRecordTreatments component not available</div>;
}

try {
  NursingTreatments = require("../Components/Nursing/NursingTreatments").default;
} catch (e) {
  NursingTreatments = () => <div className="alert alert-warning">NursingTreatments component not available</div>;
}

try {
  MolecularMedicineTreatments = require("../Components/MolecularMedicine/MolecularMedicineTreatments").default;
} catch (e) {
  MolecularMedicineTreatments = () => <div className="alert alert-warning">MolecularMedicineTreatments component not available</div>;
}


const PatientView = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  
  // View mode state - 'tables' for current grid view, 'calendar' for calendar view
  const [viewMode, setViewMode] = useState('tables');
  
  // Checkbox states
  const [activeComponents, setActiveComponents] = useState({
    treatmentDetails: true,
    laboratory: true,
    notes: true,
    diagnosticImaging: true,
    medicalRecord: true,    
    nursing: true,
    molecularMedicine: true
  });

  // Counter states
  const [counters, setCounters] = useState({
    treatmentDetails: 0,
    laboratory: 0,
    notes: 0,
    diagnosticImaging: 0,
    medicalRecord: 0,
    nursing: 0,
    molecularMedicine: 0,
  });

  // Patient data from navigation state - memoized to avoid recreation
  const patientData = useMemo(() => ({
    id: location.state?.patientId || '',
    insurance: location.state?.patientInsurance || 'Max Life Insurance',
    name: location.state?.patientName || '',
    dob: location.state?.patientDob || '',
    email: location.state?.patientEmail || '',
    phone: location.state?.patientPhone || ''
  }), [location.state]);

  // Services configuration for calendar view
  const calendarServices = useMemo(() => ({
    treatmentDetails: treatmentDetailsService,
    laboratory: laboratoryService,
    notes: notesService,
    diagnosticImaging: diagnosticImagingService,
    medicalRecord: medicalRecordService,
    nursing: nursingService,
    molecularMedicine: molecularMedicineService
  }), []);

  // Function to update counters from child components - memoized
  const updateCounter = useCallback((componentName, count) => {
    setCounters(prev => {
      // Only update if the value has changed
      if (prev[componentName] !== count) {
        return {
          ...prev,
          [componentName]: count
        };
      }
      return prev;
    });
  }, []);

  const handleCheckboxChange = useCallback((componentName) => {
    setActiveComponents(prev => ({
      ...prev,
      [componentName]: !prev[componentName]
    }));
  }, []);

  // Function to check if all components are selected - memoized
  const areAllSelected = useMemo(() => {
    return Object.values(activeComponents).every(value => value === true);
  }, [activeComponents]);

  // Function to select/deselect all
  const handleSelectAll = useCallback(() => {
    const allSelected = areAllSelected;
    const newState = {};
    
    Object.keys(activeComponents).forEach(key => {
      newState[key] = !allSelected;
    });
    
    setActiveComponents(newState);
  }, [areAllSelected, activeComponents]);

  // View mode change handlers
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  // Component configuration with their labels and counters - memoized
  const componentConfig = useMemo(() => [
    { key: 'treatmentDetails', label: 'Treatment Details', component: DetailsTreatments },
    { key: 'laboratory', label: 'Laboratory', component: LaboratoryTreatments },
    { key: 'notes', label: 'Notes', component: NotesTreatments },
    { key: 'diagnosticImaging', label: 'Diagnostic Imaging', component: DiagnosticImagingTreatments },
    { key: 'medicalRecord', label: 'Medical Record', component: MedicalRecordTreatments },
    { key: 'nursing', label: 'Nursing', component: NursingTreatments },
    { key: 'molecularMedicine', label: 'Molecular Medicine', component: MolecularMedicineTreatments }
  ], []);

  // Effect to handle initial loading if necessary
  useEffect(() => {
    // If there's initial loading logic, put it here
    setIsLoading(false);
  }, []);

  return (
    <main id="app">        
      <TopHeader />
      <div className="container-fluid w-100">
        {/* Breadcrumb */}
        <div className="mb-4">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" className="text-decoration-none">Dashboard</a>
              </li>
              <li className="breadcrumb-item">
                <a href="/patients/list" className="text-decoration-none">Patients</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">Treatment Details</li>
            </ol>
          </nav>
        </div>

        <div className="container-fluid w-100">
          {/* Patient Data Section */}
          <div className="border rounded p-4 mb-4 bg-white">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h4 className="mb-3 fw-bold">{patientData.name}</h4>
                <div className="row text-muted small">
                  <div className="col-auto">
                    <strong>ID:</strong> {patientData.id}
                  </div>
                  <div className="col-auto">|</div>
                  <div className="col-auto">
                    <strong>DOB:</strong> {patientData.dob ? new Date(patientData.dob).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit',
                      timeZone: 'UTC'
                    }) + 'T00:00:00Z' : ''}
                  </div>
                  <div className="col-auto">|</div>
                  <div className="col-auto">
                    <strong>Insurance:</strong> {patientData.insurance}
                  </div>
                </div>
                <div className="row text-muted small mt-1">
                  <div className="col-auto">
                    <strong>Email:</strong> {patientData.email}
                  </div>
                </div>
                <div className="row text-muted small mt-1">
                  <div className="col-auto">
                    <strong>Phone:</strong> {patientData.phone}
                  </div>
                </div>
              </div>
              <div className="col-md-4 text-end">
                <div className="d-flex gap-2 justify-content-end">
                  <button 
                    className={`btn ${viewMode === 'tables' ? 'btn-primary' : 'btn-outline-primary'} d-flex align-items-center gap-2`}
                    onClick={() => handleViewModeChange('tables')}
                  >
                    <i className="fas fa-table"></i>
                    Tables View
                  </button>
                  <button 
                    className={`btn ${viewMode === 'calendar' ? 'btn-primary' : 'btn-outline-primary'} d-flex align-items-center gap-2`}
                    onClick={() => handleViewModeChange('calendar')}
                  >
                    <i className="fas fa-calendar-alt"></i>
                    Calendar View
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Conditional Rendering Based on View Mode */}
          {viewMode === 'tables' ? (
            // Tables View - Current Implementation
            <>
              {/* Checkboxes Section with Counters */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Treatment Components</h5>
                    {/* Switch to Select/Deselect All */}
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="selectAllSwitch"
                        checked={areAllSelected}
                        onChange={handleSelectAll}
                      />
                      <label className="form-check-label fw-bold" htmlFor="selectAllSwitch">
                        {areAllSelected ? 'Deselect All' : 'Select All'}
                      </label>
                    </div>
                  </div>
                  
                  <div className="row">
                    {componentConfig.map((config) => (
                      <div key={config.key} className="col-md-3">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={config.key}
                            checked={activeComponents[config.key]}
                            onChange={() => handleCheckboxChange(config.key)}
                          />
                          <label className="form-check-label d-flex w-100" htmlFor={config.key}>
                            <span>{config.label}</span>
                            <span className="badge bg-danger rounded-pill ms-2">
                              {counters[config.key]}
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Treatment Components in Two Columns */}
              <main>
                <div className="row g-3">
                  {componentConfig.map((config) => {
                    if (!activeComponents[config.key]) return null;
                    
                    const Component = config.component;
                    return (
                      <div key={config.key} className="col-12 col-md-6">
                        <Component 
                          patientId={patientData.id} 
                          onCountUpdate={updateCounter}
                          componentName={config.key}
                        />
                      </div>
                    );
                  })}
                </div>
              </main>
            </>
          ) : (
            // Calendar View - PatientCalendarView Component
            <div className="mb-4">
              <PatientCalendarView 
                patientId={patientData.id}
                services={calendarServices}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default PatientView;